import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../Hooks/useAPI";
import "./ContactManagement.css";
import "./ContactAdd.css";
import { default as Header } from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import Input from "../../Components/Input/Input";
import "../../Components/Page/Page.css";
import { AxiosError } from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import debounce from "lodash.debounce";

//images
import logo from "../../Images/Logo/White Background .png";
import text from "../../Images/Logo/IceGemsText.png";

const PROFESSION_OPTIONS = [
  "Student",
  "Scientist",
  "Engineer",
  "Manufacturer",
  "Academic",
  "Government",
  "Defense",
  "Marketing",
  "Law",
  "Health",
  "Other",
];

const INTEREST_OPTIONS = [
  "Engines",
  "Software",
  "Research",
  "Manufacturing",
  "Public Policy",
  "Climate",
  "Economics",
  "Health",
  "Other",
];

interface RegisterFormData {
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  streetaddress1: string;
  streetaddress2: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  companyName?: string;
  companyType?: string;
  title?: string;
  profession: string[];
  professionOther?: string;
  interests: string[];
  interestsOther?: string;
  ipaddress: string;
  phonenocode: string;
  datasource: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  profession?: string;
  interests?: string;
  agreeToTerms?: string;
  general?: string;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

export default function ContactAdd() {
  const navigate = useNavigate();
  const {
    getStateProvince,
    saveContact,
    checkEmailExists,
    checkContactForMatch,
    getContactByCIDN,
    contactMatchbyCIDN,
  } = useAPI();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: "",
    firstName: "",
    middleName: "",
    lastName: "",
    streetaddress1: "",
    streetaddress2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    companyName: "",
    companyType: "",
    title: "",
    profession: [],
    professionOther: "",
    interests: [],
    interestsOther: "",
    ipaddress: "",
    phonenocode: "",
    datasource: "contactmanagement",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [, setEmail] = useState("");
  const [, setFirstName] = useState("");
  const [, setMiddleName] = useState("");
  const [, setLastName] = useState("");
  const [, setAddress1] = useState("");
  const [, setAddress2] = useState("");
  const [, setCity] = useState("");
  const [, setState] = useState("");
  const [, setZip] = useState("");
  const [, setCountry] = useState("");
  const [, setPhone] = useState("");
  const [, setCountryCode] = useState("");
  const [, setCompanyName] = useState("");
  const [, setCompanyType] = useState("");
  const [, setTitle] = useState("");
  const [, setDataSourceDescription] = useState("");
  const [, setProfession] = useState<string[]>([]);
  const [, setProfessionOther] = useState("");
  const [, setInterests] = useState<string[]>([]);
  const [, setInterestsOther] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [, setPhoneNoCode] = useState("");
  const [states, setStates] = useState<StateOption[]>([]);
  const [cidn, setCIDN] = useState<number | null>(null);

  // --- Initialize contact ID from localStorage ---
  useEffect(() => {
    const storedId = Number(localStorage.getItem("partial_contact_id"));
    if (storedId) {
      setCIDN(storedId);
    }
  }, []);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  const fieldRefs = {
    firstName: useRef<HTMLInputElement>(null),
    lastName: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    profession: useRef<HTMLDivElement>(null),
    interests: useRef<HTMLDivElement>(null),
    agreeToTerms: useRef<HTMLInputElement>(null),
  };

  const isFormValid = (): boolean => {
    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      if (name === "agreeToTerms") {
        setFormData((prev) => {
          const updated = {
            ...prev,
            [name]: checked, // checkbox value
          };

          // debouncedSaveRef(updated, cidn, name); // send updated data

          return updated;
        });
      } else if (name === "profession" || name === "interests") {
        const optionValue = value;

        setFormData((prev) => {
          const currentArray = prev[name] as string[];

          const updatedArray = checked
            ? [...currentArray, optionValue]
            : currentArray.filter((item) => item !== optionValue);

          const updated = {
            ...prev,
            [name]: updatedArray,
          };

          // Send to debounced auto-save with the REAL updated data
          //debouncedSaveRef(updated, cidn, name);

          return updated;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "age" ? (value ? Number(value) : undefined) : value,
      }));

      // Update form state
      if (name === "state") {
        setFormData((prev) => {
          const updated = { ...prev, [name]: value };

          // Send to debounced auto-save
          //debouncedSaveRef(updated, cidn, name);

          return updated;
        });
      }
    }

    // Clear error for this field when user changes it
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhoneChange = (
    value: string,
    data: { dialCode: string; countrycode: string },
  ) => {
    const countrycode = data.dialCode; // e.g. "1"
    const phonenocode = value.replace(`${countrycode}`, "");

    setPhoneNumber(value);
    setPhoneNoCode(phonenocode);

    setFormData((prev) => ({
      ...prev,
      phone: value,
      countrycode: countrycode,
      phonenocode: phonenocode,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Profession validation
    if (formData.profession.length === 0) {
      newErrors.profession = "Please select at least one profession";
      isValid = false;
    }

    // Interests validation
    if (formData.interests.length === 0) {
      newErrors.interests = "Please select at least one interest";
      isValid = false;
    }

    setErrors(newErrors);

    const firstErrorKey = Object.keys(newErrors)[0] as keyof typeof fieldRefs;

    const el = fieldRefs[firstErrorKey]?.current;

    if (el) {
      const headerOffset = 90; // header height
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      el.focus?.();
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear general error
    setErrors((prev) => ({ ...prev, general: undefined }));

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    const formDataToSubmit = {
      ...formData,
      cidn: cidn ?? undefined, // include it only if it exists
    };

    try {
      const response = await checkContactForMatch(formDataToSubmit, true);
      const out_cidn = response.out_cidn;
      if (out_cidn === 0) {
        const data = await saveContact(formDataToSubmit, false, null);
        const newcidn = data.out_cidn;

        //check match status
        //Add record to matchstatus table if there is a match
        contactMatchbyCIDN(newcidn);

        // // Store the token and user data
        // localStorage.setItem("token", data.token);
        // localStorage.setItem("user", JSON.stringify(data.user));

        const addedMessage =
          "Contact info for " +
          formData.firstName +
          " " +
          formData.lastName +
          " has been added";
        alert(addedMessage);
        // Redirect to home page
        navigate("/contact-management");
      } else {
        let existsMessage = "This contact already exists:\n";
        existsMessage += `cidn ${out_cidn}\n`;
        const contactresponse = await getContactByCIDN(out_cidn);

        if (contactresponse?.data && contactresponse.data.length > 0) {
          const existingcontact = contactresponse.data[0];
          if (existingcontact) {
            const {
              firstname,
              middlename,
              lastname,
              email,
              streetaddress,
              streetaddress2,
              city,
              state,
              zip,
              country,
              dateadded,
              datasource,
            } = existingcontact;

            existsMessage +=
              `
${[firstname, middlename, lastname].filter(Boolean).join(" ")}
${email ?? ""}
${[streetaddress, streetaddress2].filter(Boolean).join(" ")}
${city ?? ""}${city && state ? ", " : ""}${state ?? ""} ${zip ?? ""} ${country ?? ""}
Added on ${dateadded ?? ""}
DataSource: ${datasource ?? ""}
`.trim() + "\n";
          }
        }

        alert(existsMessage);
      }
    } catch (err) {
      // Check if it's an Axios error with response data
      const axiosError = err as AxiosError<{ message: string }>;

      if (axiosError.response?.data?.message) {
        const errorMessage = axiosError.response.data.message;

        // Check for exact email already registered error
        if (errorMessage === "Email already registered") {
          // Set error on the email field
          setErrors((prev) => ({
            ...prev,
            email: "This Email is already registered",
          }));

          // Scroll to email field
          const el = fieldRefs.email.current;

          if (el) {
            const headerOffset = 90;
            const elementPosition = el.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.scrollY - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });

            el.focus();
          }

          return;
        } else {
          // Set as general error for other cases
          setErrors((prev) => ({
            ...prev,
            general: errorMessage,
          }));
        }
      } else {
        // Fallback for other types of errors
        setErrors((prev) => ({
          ...prev,
          general: err instanceof Error ? err.message : "An error occurred",
        }));
      }
    }
  };

  // --- Stable debounced saveContact function ---
  const debouncedSaveRef = React.useRef(
    debounce(
      async (
        updatedData: RegisterFormData,
        cidn: Number | null,
        fieldname: string | null,
      ) => {
        try {
          //console.log("payload: ", updatedData);
          //console.log("fieldname: ", fieldname);

          const bReturnAll = fieldname === "email"; //set breturnall to true if fieldname is email

          const response = await saveContact(updatedData, bReturnAll, cidn);

          const out_cidn = response.out_cidn;

          //Only update all the fields in the page if we have a match by email address
          if (bReturnAll) {
            const returnedEmail = response?.email;
            const returnedFirstName = response?.firstname;
            const returnedMiddleName = response?.middlename;
            const returnedLastName = response?.lastname;
            const returnedAddress1 = response?.address1;
            const returnedAddress2 = response?.address2;
            const returnedCity = response?.city;
            const returnedState = response?.state;
            const returnedZip = response?.zip;
            const returnedCountry = response?.country;
            const returnedPhone = response?.phone;
            const returnedCountryCode = response?.countrycode;
            const returnedCompanyName = response?.companyname;
            const returnedCompanyType = response?.companytype;
            const returnedTitle = response?.title;
            const returnedDatasource = "contactmanagement";
            const returnedProfession = response?.profession;
            const returnedProfessionOther = response?.profession_other;
            const returnedInterests = response?.interests;
            const returnedInterestsOther = response?.interests_other;
            const returnedAgreeToTerms = response?.termsconditions;

            const professionArray = parsePgArray(returnedProfession);
            const interestsArray = parsePgArray(returnedInterests);

            setEmail(returnedEmail);
            setFirstName(returnedFirstName);
            setMiddleName(returnedMiddleName);
            setLastName(returnedLastName);
            setAddress1(returnedAddress1);
            setAddress1(returnedAddress2);
            setCity(returnedCity);
            setState(returnedState);
            setZip(returnedZip);
            setCountry(returnedCountry);
            setPhone(returnedPhone);
            setPhoneNoCode(returnedPhone);
            if (returnedCountryCode && returnedPhone) {
              setPhoneNumber(`${returnedCountryCode}${returnedPhone}`);
            }
            setCountryCode(returnedCountryCode);
            setCompanyName(returnedCompanyName);
            setCompanyType(returnedCompanyType);
            setTitle(returnedTitle);
            setDataSourceDescription(returnedDatasource);
            setProfession(professionArray);
            setProfessionOther(returnedProfessionOther);
            setInterests(interestsArray);
            setInterestsOther(returnedInterestsOther);

            // Update the form field so the input shows it
            setFormData((prev) => ({
              ...prev,
              email: returnedEmail || "",
              firstName: returnedFirstName || "",
              middleName: returnedMiddleName || "",
              lastName: returnedLastName || "",
              streetaddress1: returnedAddress1 || "",
              streetaddress2: returnedAddress2 || "",
              city: returnedCity || "",
              state: returnedState || "",
              zip: returnedZip || "",
              country: returnedCountry || "",
              phone: returnedPhone || "",
              phoneNumber: returnedPhone || "",
              countrycode: returnedCountryCode || "",
              companyName: returnedCompanyName || "",
              companyType: returnedCompanyType || "",
              title: returnedTitle || "",
              datasource: returnedDatasource || "contactmanagement",
              profession: professionArray,
              professionOther: returnedProfessionOther || "",
              interests: interestsArray,
              interestsOther: returnedInterestsOther || "",
            }));
          }

          // If new partial ID returned, store it
          const returnedId = out_cidn;

          if (returnedId && !cidn) {
            localStorage.setItem("partial_contact_id", returnedId);
            setCIDN(returnedId);
          }
        } catch (error) {
          console.error("Error auto-saving contact:", error);
        }
      },
      600,
    ), // control debounce timing here
  ).current;

  const parsePgArray = (value: any): string[] => {
    if (!value) return [];

    // already a JS array
    if (Array.isArray(value)) return value;

    // If comes back as PG array: {"Engineer","Scientist"}
    if (typeof value === "string") {
      const trimmed = value.replace(/^{|}$/g, "").replace(/"/g, "");
      if (!trimmed) return [];
      return trimmed.split(",").map((v) => v.trim());
    }

    return [];
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Copy of updated form data
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // If the field is email and has a value, check for duplicates
      if (name === "email" && value) {
        (async () => {
          try {
            const exists = await checkEmailExists(value);

            if (exists) {
              console.log("Email already exists");
              setErrors((prevErrors) => ({
                ...prevErrors,
                email: "This Email is already registered",
              }));
            } else {
              //console.log("Email is available");
              setErrors((prevErrors) => ({
                ...prevErrors,
                email: "",
              }));
            }
          } catch (err) {
            console.error("Error checking email:", err);
          }
        })();
      }

      // Trigger debounced auto-save regardless of email check
      //debouncedSaveRef(updated, cidn, name);

      return updated;
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("partial_contact_id");
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedSaveRef.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      debouncedSaveRef.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      // Component is unmounting → user left the page
      localStorage.removeItem("partial_contact_id");
    };
  }, []);

  return (
    <div className="generalPage">
      <Header />

      <div className="pageContainer">
        <div className="contactadd-table-container">
          <form onSubmit={handleSubmit}>
            <div className="contactadd-form-group">
              <table className="contactadd-table">
                <thead>
                  <tr>
                    <th colSpan={6} className="contactadd-table-section_header">
                      Add a new Contact
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>First Name *</th>
                    <td id="profile-firstName">
                      {" "}
                      <Input
                        label=""
                        type="text"
                        id="firstName"
                        ref={fieldRefs.firstName}
                        placeholder="Enter first name"
                        required={true}
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.firstName && (
                        <div className="field-error">{errors.firstName}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Middle Name</th>
                    <td id="profile-middleName">
                      <Input
                        label=""
                        type="text"
                        id="middleName"
                        placeholder="Enter middle name"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Last Name *</th>
                    <td id="profile-lastName">
                      {" "}
                      <Input
                        label=""
                        type="text"
                        id="lastName"
                        placeholder="Enter last name"
                        required={true}
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.lastName && (
                        <div className="field-error">{errors.lastName}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Email *</th>
                    <td id="profile-email">
                      {" "}
                      <Input
                        label=""
                        type="email"
                        id="email"
                        ref={fieldRefs.email}
                        placeholder="Enter Email"
                        required={true}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.email && (
                        <div className="field-error">{errors.email}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Address Line 1</th>
                    <td id="profile-address1">
                      <Input
                        label=""
                        type="text"
                        id="streetaddress1"
                        placeholder="Enter address line 1"
                        name="streetaddress1"
                        value={formData.streetaddress1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Address Line 2</th>
                    <td id="profile-address2">
                      <Input
                        label=""
                        type="text"
                        id="streetaddress2"
                        placeholder="Enter address line 2"
                        name="streetaddress2"
                        value={formData.streetaddress2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>City</th>
                    <td id="profile-city">
                      <Input
                        label=""
                        type="text"
                        id="city"
                        placeholder="Enter city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>State</th>
                    <td id="profile-state">
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                      >
                        <option value="">-- Select State --</option>
                        {states.map((s) => (
                          <option
                            key={s.stateprovinceabbrv}
                            value={s.stateprovinceabbrv}
                          >
                            {s.stateprovincename}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>Zip</th>
                    <td id="profile-zip">
                      <Input
                        label=""
                        type="text"
                        id="zip"
                        placeholder="Enter zip code"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Country</th>
                    <td id="profile-country">
                      <Input
                        label=""
                        type="text"
                        id="country"
                        placeholder="Enter country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td id="profile-phone">
                      <PhoneInput
                        country={"us"}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        onBlur={handleBlur}
                        inputClass="phone-input"
                        containerClass="phone-input-container"
                        buttonClass="phone-input-button"
                        dropdownClass="phone-input-dropdown"
                        searchClass="phone-input-search"
                        placeholder="Enter phone number"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Company</th>
                    <td id="profile-company">
                      <Input
                        label=""
                        type="text"
                        id="companyName"
                        placeholder="Enter company name"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Company Type</th>
                    <td id="profile-companytype">
                      <Input
                        label=""
                        type="text"
                        id="companyType"
                        placeholder="Enter company type"
                        name="companyType"
                        value={formData.companyType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Title</th>
                    <td id="profile-title">
                      <Input
                        label=""
                        type="text"
                        id="title"
                        placeholder="Enter title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      Profession<br></br> (Select at least one)
                    </th>
                    <td id="profile-profession">
                      <div className="checkbox-group">
                        <div ref={fieldRefs.profession}>
                          {PROFESSION_OPTIONS.map((option) => (
                            <div key={option} className="checkbox-item">
                              <label htmlFor={`profession-${option}`}>
                                <input
                                  type="checkbox"
                                  id={`profession-${option}`}
                                  name="profession"
                                  value={option}
                                  checked={formData.profession.includes(option)}
                                  onChange={handleChange}
                                  className={errors.profession ? "error" : ""}
                                />{" "}
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      {errors.profession && (
                        <div className="field-error">{errors.profession}</div>
                      )}
                    </td>
                  </tr>
                  {formData.profession.includes("Other") && (
                    <tr>
                      <th>Other Profession</th>
                      <td id="profile-professionother">
                        <Input
                          label=""
                          type="text"
                          id="professionOther"
                          placeholder="Specify profession"
                          name="professionOther"
                          value={formData.professionOther}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>
                      Interests<br></br> (Select at least one)
                    </th>
                    <td id="profile-interests">
                      <div className="checkbox-group">
                        <div ref={fieldRefs.interests}>
                          {INTEREST_OPTIONS.map((option) => (
                            <div key={option} className="checkbox-item">
                              <label htmlFor={`interests-${option}`}>
                                <input
                                  type="checkbox"
                                  id={`interests-${option}`}
                                  name="interests"
                                  value={option}
                                  checked={formData.interests.includes(option)}
                                  onChange={handleChange}
                                  className={errors.interests ? "error" : ""}
                                />{" "}
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      {errors.interests && (
                        <div className="field-error">{errors.interests}</div>
                      )}
                    </td>
                  </tr>
                  {formData.interests.includes("Other") && (
                    <tr>
                      <th>Other Interests</th>
                      <td id="profile-interestother">
                        <Input
                          label=""
                          type="text"
                          id="interestsOther"
                          placeholder="Specify interests"
                          name="interestsOther"
                          value={formData.interestsOther}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={2}>
                      <br></br>
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <button
                          id="submit"
                          type="submit"
                          className={`contactadd-submit-button ${
                            isFormValid()
                              ? "contactadd-submit-button-active"
                              : ""
                          }`}
                          disabled={!isFormValid()}
                        >
                          Add Contact
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
